import { ScrollText, Sparkles, Camera, MapPinned, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";

// Marketing Alignment v1 — STEP_KEYS now reflect the canonical NorthLights
// loop: Read the clue → Find the spark → Capture the Spark → Light the
// Spirit Map → Unlock the next message. The i18n key NAMES are
// unchanged (so non-English locales keep rendering with their existing
// strings until Marketing Alignment v1.1 ports them); only the icon
// vocabulary and the English content in en.json are aligned in this
// pass. Order is preserved.
const STEP_KEYS = [
  { key: "spinUp", icon: ScrollText },
  { key: "invitePlayers", icon: Sparkles },
  { key: "cluesDaily", icon: Camera },
  { key: "spiritMap", icon: MapPinned },
  { key: "santaWrites", icon: Mail },
] as const;

export function HowItWorks() {
  const { t } = useTranslation();
  return (
    <section
      id="how-it-works"
      className="relative px-5 py-12 sm:px-8 sm:py-16 lg:px-12 lg:py-20"
      data-testid="section-how"
    >
      <div className="mx-auto max-w-7xl">
        <div className="jingle-reveal mx-auto max-w-2xl text-center">
          <p className="inline-flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.32em] text-jingle-gold">
            <span aria-hidden className="jingle-flourish-line" />
            <span aria-hidden className="text-jingle-gold-soft/80">✦</span>
            {t("marketing.how.eyebrow")}
            <span aria-hidden className="text-jingle-gold-soft/80">✦</span>
            <span aria-hidden className="jingle-flourish-line jingle-flourish-line--right" />
          </p>
          <h2 className="mt-3 font-serif text-4xl font-semibold tracking-tight text-jingle-snow sm:text-5xl">
            {t("marketing.how.heading")}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-jingle-snow/75 sm:text-lg">
            {t("marketing.how.intro")}
          </p>
        </div>

        <ol className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {STEP_KEYS.map((step, i) => {
            const Icon = step.icon;
            const isLast = i === STEP_KEYS.length - 1;
            // 5 cards in a 2-col `sm` grid leaves the last card alone in
            // row 3; span+center it so the empty cell doesn't read as a gap.
            const lastFix = isLast
              ? "sm:col-span-2 sm:mx-auto sm:max-w-sm lg:col-span-1 lg:mx-0 lg:max-w-none"
              : "";
            return (
              <li
                key={step.key}
                className={`jingle-reveal jingle-card-pine jingle-lift relative rounded-2xl p-6 ${lastFix}`}
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <span className="absolute right-4 top-4 font-serif text-3xl text-jingle-gold/40">
                  0{i + 1}
                </span>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-jingle-gold/15 text-jingle-gold">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-serif text-xl font-semibold text-jingle-snow">
                  {t(`marketing.how.${step.key}.title`)}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-jingle-snow/70">
                  {t(`marketing.how.${step.key}.body`)}
                </p>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}
